// @givmo/app-charity — the ROOT, which never renders, and the ONE landing computation every
// "my landing" control shares (CPA-WEB40 · shell contract v3).
//
// ══ THE ROOT IS A DOORWAY, NOT A PAGE ══════════════════════════════════════════════════════════
//
// `/` is exactly one redirect, `replace: true`, to wherever this person belongs. Root stays
// addressable so existing bookmarks keep working.
//
// ══ THE RULE IS THE PACKAGE'S, AND THIS FILE ONLY SUPPLIES ITS INPUTS ══════════════════════════
//
// `landingDestination` (`@givmo/org-console`) is the ONE pure rule for both class consoles: the
// admin's CHOSEN default app → (on a COLD entry only) the LAST app they were in → the only openable
// app → the CLASS DEFAULT (`donations`, §9-A A1: EVERY charity admin, a Global Admin included; the
// Admin Console tile never counts as openable) → the apps page. There is no Owner → `/org` branch
// and no "nothing reported ⇒ the grid": a dark or unreported standing still lands the class
// default — `/donations` — where the module's own gate answers for itself (a transport failure
// renders the unavailable notice with a retry there, in place). This host hands in the chosen
// default off the ONE standing read, the last-module hint off this device, the derived squares
// and the class default's slug.
//
// The one thing this host adds is WHEN the doorway may decide: the memberships read must have
// settled (a failed one goes to `/apps`, which renders the error + retry in place of the
// statement) and the standing read must have settled (so the chosen default and the hint apply
// rather than a first-frame guess). A caller with NO organization is the class door's, not this
// component's: `org/claim-first-gate.tsx` mounts the doorway while the memberships read is pending
// and swaps in its `/claim` redirect the moment the read lands empty — so on `loaded-empty` this
// doorway decides NOTHING. A decision there would race the door's replace with one of its own: a
// synchronous navigation loop under dev-mode effect reconnection (a frozen tab on the first screen
// of every new sign-in) and the claim wizard under `/donations` in production.
//
// ══ TWO ENTRY KINDS ════════════════════════════════════════════════════════════════════════════
//
// The ROOT is a `cold` entry — a bookmark, a typed address, the post-sign-in return — and it is the
// only reader of the last-module hint. The Givmo mark and the not-found page's control are `in-app`
// entries: "wherever you are, click the mark, you get your default module" — they skip the hint.
//
// `replace` rather than a push at the root, and `location.replace` for a cross-origin hop, because
// a pushed navigation leaves a back-button trap.

import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useRouter, useRouterState } from '@tanstack/react-router';
import {
  currentModuleFor,
  landingDestination,
  readLastModule,
  writeLastModule,
  type LandingDestination,
  type LandingEntry,
} from '@givmo/org-console';
import { useAuth } from '../auth/auth-context';
import { useCharityConsole } from '../console/charity-console-context';
import { APPS_ROUTE } from '../console/console-routes';
import { CLASS_DEFAULT_SLUG, charityModuleRoutes } from '../console/module-table';
import { useConsoleSquares, useModuleNavigate } from '../console/use-console-squares';
import { useActiveOrg } from '../org/active-org-context';

export interface LandingOptions {
  /**
   * Compute what "Default" resolves to — the rule with NO chosen default (the preferences page's
   * hint line). The ordinary call honours the person's choice.
   */
  ignoreChosenDefault?: boolean;
}

/** The landing rule's answer for this person, with the slug of the app it names when it names one. */
export interface Landing extends LandingDestination {
  /** The square the destination belongs to, or `null` for the apps page. */
  slug: string | null;
  /** That square's label, for copy ("Right now that is Donations."). */
  label: string | null;
}

/**
 * Where this person's landing is, for one entry kind.
 *
 * The hint is read only for a `cold` entry, per (person, organization): a uid-less or
 * organization-less caller reads none.
 */
export function useLandingDestination(entry: LandingEntry, options: LandingOptions = {}): Landing {
  const { user } = useAuth();
  const { orgRef, read } = useCharityConsole();
  const squares = useConsoleSquares();
  const uid = user?.uid ?? null;
  const ignoreChosen = options.ignoreChosenDefault === true;

  return useMemo(() => {
    const lastModule = entry === 'cold' && orgRef !== null ? readLastModule(uid, orgRef) : null;
    const destination = landingDestination({
      entry,
      preferredModule: ignoreChosen ? null : read.defaultLandingModule,
      lastModule,
      squares,
      classDefault: CLASS_DEFAULT_SLUG,
      appsPath: APPS_ROUTE,
    });
    const square =
      destination.branch === 'apps'
        ? undefined
        : squares.find((candidate) => candidate.destination?.href === destination.href);
    return {
      ...destination,
      slug: square?.row.slug ?? null,
      label: square?.row.label ?? null,
    };
  }, [entry, ignoreChosen, orgRef, read.defaultLandingModule, squares, uid]);
}

/**
 * Follow a landing: a cross-origin destination keeps the whole-document hop; an in-app one is a
 * router navigation — `replace` at the doorway, a push from a control. Forwarding INTO an app
 * writes the last-module hint (T1.5) — for an in-app hop that is `useRememberCurrentModule`'s job,
 * because the shell mounts on the page the hop lands on; an EXTERNAL hop leaves this document, so
 * the hint is written here, before it. ⚠ No charity row is external today (pinned in
 * `module-table.test.ts`); the branch exists so the first external row keeps its hint.
 */
function useFollowLanding(): (landing: Landing, replace: boolean) => void {
  const { user } = useAuth();
  const { orgRef } = useCharityConsole();
  const navigate = useNavigate();
  const router = useRouter();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const goInApp = useModuleNavigate();
  const uid = user?.uid ?? null;

  return useCallback(
    (landing: Landing, replace: boolean) => {
      if (landing.kind === 'external') {
        if (landing.slug !== null && orgRef !== null) writeLastModule(uid, orgRef, landing.slug);
        window.location.replace(landing.href);
        return;
      }
      if (replace) {
        // The doorway is a module switch: the destination opens in its OWN frame (OM-18 4(b)).
        void navigate({ to: landing.href, replace: true, state: { sidebarFrame: null } });
        return;
      }
      // The `current`-square rule (T1.7): when the mark's answer IS the page you are on, the click
      // is still a real action — it reloads the page's data rather than doing nothing.
      if (landing.href === pathname) {
        void router.invalidate();
        return;
      }
      goInApp(landing.href);
    },
    [goInApp, navigate, orgRef, pathname, router, uid],
  );
}

/**
 * The "my landing" control's act (T1.7): the Givmo mark in every chrome and the not-found page's
 * "Back to your apps". An `in-app` entry — the hint is never consulted — and a push, not a replace.
 */
export function useGoToLanding(): () => void {
  const landing = useLandingDestination('in-app');
  const follow = useFollowLanding();
  return useCallback(() => follow(landing, false), [follow, landing]);
}

/**
 * Remember which module this person is in, for the next cold entry (T1.5). Called by the shell on
 * every pathname; writes only when the address is inside a mounted module.
 *
 * `frame` is the module whose FRAME the shell rendered (`Sidebar.frame`). On a page BORROWED into
 * another module's frame the hint is the FRAME, not the address's module (Org Machinery OM-18
 * design 4(c)): the person is IN the borrower, so a reload keeps the frame, a cold entry through
 * the doorway returns them there, and a module's own in-page link — which carries no frame —
 * keeps the frame through the hint. Everywhere else the frame IS the address's module.
 */
export function useRememberCurrentModule(pathname: string, frame?: string): void {
  const { user } = useAuth();
  const { orgRef } = useCharityConsole();
  const uid = user?.uid ?? null;
  useEffect(() => {
    if (orgRef === null) return;
    const slug = currentModuleFor(pathname, charityModuleRoutes());
    if (slug !== undefined) writeLastModule(uid, orgRef, frame ?? slug);
  }, [frame, orgRef, pathname, uid]);
}

/** The root route's component. Renders nothing, ever — it forwards and unmounts. */
export function ConsoleLanding() {
  const { membershipsState } = useActiveOrg();
  const { pending } = useCharityConsole();
  const landing = useLandingDestination('cold');
  const follow = useFollowLanding();

  // The doorway decides only once both reads have settled (see the header): a failed memberships
  // read goes to the apps page, which renders the error and a retry in place of the statement; a
  // read that answers NO organization is the class door's caller — never decided here.
  const decision = useMemo<Landing | null>(() => {
    if (membershipsState === 'pending' || membershipsState === 'loaded-empty') return null;
    if (membershipsState === 'error') {
      return { kind: 'in-app', href: APPS_ROUTE, branch: 'apps', slug: null, label: null };
    }
    if (pending) return null;
    return landing;
  }, [membershipsState, pending, landing]);

  useEffect(() => {
    if (decision === null) return;
    follow(decision, true);
  }, [decision, follow]);

  // Deliberately nothing. A spinner here would be a frame of chrome that exists only to be replaced,
  // and the shell around this has already been painted by the time it mounts.
  return null;
}
